import {
  INPUT_MONO,
  TABLE,
  TD,
  TH,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { ImpactStrings } from "@/components/tools/impact-factor-calculator";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import type { MetricResult } from "@/lib/tool-kit/reference-tools/journal-metrics";

/*
 * The Impact Factor Calculator's count tables and result cards. Every word comes from
 * the widget's strings.
 */

export interface MetricView extends MetricResult {
  citationParts: number[];
  itemParts: number[];
}

export function CountTable({
  kind,
  heading,
  intro,
  years,
  metricYear,
  citations,
  items,
  onCitations,
  onItems,
  idPrefix,
  strings,
}: {
  kind: "jif" | "citeScore";
  heading: string;
  intro: string;
  /** The publication year of each row. */
  years: readonly number[];
  metricYear: number;
  citations: readonly string[];
  items: readonly string[];
  onCitations: (index: number, value: string) => void;
  onItems: (index: number, value: string) => void;
  idPrefix: string;
  strings: ImpactStrings;
}) {
  const first = metricYear - 3;
  const jif = kind === "jif";
  const citationHead = jif
    ? t(strings.jifCitations, { year: metricYear })
    : t(strings.csCitations, { first, year: metricYear });
  const itemHead = jif ? strings.jifItems : strings.csDocuments;
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <p className="text-muted-foreground text-sm">{intro}</p>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.publishedIn}
              </th>
              <th className={TH} scope="col">
                {citationHead}
              </th>
              <th className={TH} scope="col">
                {itemHead}
              </th>
            </tr>
          </thead>
          <tbody>
            {years.map((published, index) => {
              const citationId = `${idPrefix}-c-${index}`;
              const itemId = `${idPrefix}-n-${index}`;
              const citationLabel = jif
                ? t(strings.jifCitationsLabel, { year: metricYear, published })
                : t(strings.csCitationsLabel, {
                    first,
                    year: metricYear,
                    published,
                  });
              const itemLabel = jif
                ? t(strings.jifItemsLabel, { published })
                : t(strings.csDocumentsLabel, { published });
              return (
                <tr key={published}>
                  <th className={`${TD} font-medium`} scope="row">
                    {published}
                    {jif ? (
                      <span className="block font-normal text-muted-foreground text-xs">
                        {index < 2 ? strings.jifWindow : strings.fiveYearWindow}
                      </span>
                    ) : null}
                  </th>
                  <td className={TD}>
                    <label className="sr-only" htmlFor={citationId}>
                      {citationLabel}
                    </label>
                    <input
                      className={INPUT_MONO}
                      id={citationId}
                      inputMode="numeric"
                      onChange={(event) =>
                        onCitations(index, event.target.value)
                      }
                      type="text"
                      value={citations[index] ?? ""}
                    />
                  </td>
                  <td className={TD}>
                    <label className="sr-only" htmlFor={itemId}>
                      {itemLabel}
                    </label>
                    <input
                      className={INPUT_MONO}
                      id={itemId}
                      inputMode="numeric"
                      onChange={(event) => onItems(index, event.target.value)}
                      type="text"
                      value={items[index] ?? ""}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function MetricResultCard({
  title,
  view,
  missing,
  fmt,
  strings,
}: {
  title: string;
  view: MetricView | null;
  missing: string;
  fmt: NumberFormat;
  strings: ImpactStrings;
}) {
  if (!view) {
    return (
      <div className="space-y-1">
        <p className="font-medium text-foreground">{title}</p>
        <p className="text-muted-foreground text-sm">{missing}</p>
      </div>
    );
  }
  const sum = (parts: readonly number[]) =>
    parts.length > 1
      ? t(strings.sums, {
          parts: parts.map((part) => fmt(part)).join(strings.plus),
        })
      : fmt(parts[0] ?? 0);
  return (
    <div className="space-y-1">
      <p className="font-medium text-foreground">{title}</p>
      <p className="font-semibold text-2xl text-foreground">
        {fmt(view.value, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })}
      </p>
      <p className="text-muted-foreground text-sm">
        {t(strings.exact, {
          value: fmt(view.value, {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          }),
        })}
      </p>
      <p className="break-words font-mono text-foreground text-xs">
        {t(strings.working, {
          numerator: sum(view.citationParts),
          denominator: sum(view.itemParts),
          value: fmt(view.value, { maximumFractionDigits: 3 }),
        })}
      </p>
      <p className="font-mono text-muted-foreground text-xs">
        {t(strings.working, {
          numerator: fmt(view.numerator),
          denominator: fmt(view.denominator),
          value: fmt(view.value, { maximumFractionDigits: 3 }),
        })}
      </p>
    </div>
  );
}
